import { ADMIN_DEFAULT_LANGUAGE, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from "@pulse/i18n";
import i18n, { ensureAdminI18n } from "@/lib/i18next";
import { persistConsumerPreferredLanguage } from "@/lib/userPreferences";
import { supabase } from "@/lib/supabase";

export const CONSUMER_LANGUAGE_STORAGE_KEY = "pulse.language.consumer";
export const VENDOR_LANGUAGE_STORAGE_KEY = "pulse.language.vendor";
export const ADMIN_LANGUAGE_STORAGE_KEY = "pulse.language.admin";
export const VENDOR_DEFAULT_LANGUAGE: SupportedLanguage = "en";

export function readStoredLanguage(key: string): SupportedLanguage {
  if (typeof window === "undefined") {
    if (key === ADMIN_LANGUAGE_STORAGE_KEY) return ADMIN_DEFAULT_LANGUAGE;
    if (key === VENDOR_LANGUAGE_STORAGE_KEY) return VENDOR_DEFAULT_LANGUAGE;
    return DEFAULT_LANGUAGE;
  }
  try {
    const raw = window.localStorage.getItem(key);
    const code = raw?.slice(0, 2);
    if (code && SUPPORTED_LANGUAGES.includes(code as SupportedLanguage)) {
      return code as SupportedLanguage;
    }
  } catch {
    /* ignore storage access errors */
  }
  if (key === ADMIN_LANGUAGE_STORAGE_KEY) return ADMIN_DEFAULT_LANGUAGE;
  return key === VENDOR_LANGUAGE_STORAGE_KEY ? VENDOR_DEFAULT_LANGUAGE : DEFAULT_LANGUAGE;
}

export function applyStoredLanguage(key: string): void {
  const lang = readStoredLanguage(key);
  if (i18n.language?.slice(0, 2) !== lang) {
    void i18n.changeLanguage(lang).then(() => {
      if (key === ADMIN_LANGUAGE_STORAGE_KEY) return ensureAdminI18n(lang);
    });
  } else if (key === ADMIN_LANGUAGE_STORAGE_KEY) {
    void ensureAdminI18n(lang);
  }
}

async function mirrorConsumerLanguageToDb(lang: SupportedLanguage): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    if (!userId) return;
    await persistConsumerPreferredLanguage(userId, lang);
  } catch {
    /* best-effort; localStorage remains source for UI */
  }
}

export function persistAndApplyLanguage(key: string, lang: SupportedLanguage): void {
  try {
    window.localStorage.setItem(key, lang);
  } catch {
    /* ignore storage access errors */
  }
  void i18n.changeLanguage(lang).then(() => {
    if (key === ADMIN_LANGUAGE_STORAGE_KEY) return ensureAdminI18n(lang);
  });
  if (key === CONSUMER_LANGUAGE_STORAGE_KEY) {
    void mirrorConsumerLanguageToDb(lang);
  }
}
